/**
 * useEventMockStore.ts
 *
 * In-memory Zustand store for event UI mockups.
 * No persistence — everything resets on refresh.
 */

import { create } from 'zustand';
import { INITIAL_MOCK_EVENTS, type MockEvent } from '../utils/eventMockData';
import type { LocationBody } from '../services/locationService';

export interface CreateEventPayload {
  event_title: string;
  info: string;
  event_structure_type: 'single' | 'external';
  event_ticketing_type: 'internal' | 'external';
  external_ticketing_url?: string;
  start: string;
  end: string;
  timezone: string;

  event_age?: string;
  genres: string[];
  images: string[];
  medias?: { file_url: string }[];
  tickets?: {
    ticket_type: string;
    price: number;
    quantity: number;
    description?: string;
    age_requirement?: string;
    sales_start_date?: string;
    sales_start_time?: string;
    sales_end_date?: string;
    sales_end_time?: string;
    has_end_purchase_period?: boolean;
    sub_events?: boolean;
    multi_day_access?: boolean;
  }[];
  guests?: { email: string }[];
  collaboratorIds: number[];
  is_draft_or_published: 'draft' | 'published';
  locationBody: LocationBody | null;
  locationDetails?: string;
}

interface EventMockState {
  events: MockEvent[];
  createEvent: (payload: CreateEventPayload) => MockEvent;
  updateEvent: (id: number, patch: Partial<MockEvent>) => void;
  cancelEvent: (id: number) => void;
  getEventById: (id: number) => MockEvent | undefined;
  getMyEvents: () => MockEvent[];
}

export const useEventMockStore = create<EventMockState>((set, get) => ({
  events: INITIAL_MOCK_EVENTS,

  createEvent: (payload) => {
    const newEvent: MockEvent = {
      id: Date.now(),
      event_title: payload.event_title,
      info: payload.info,
      event_structure_type: payload.event_structure_type,
      event_ticketing_type: payload.event_ticketing_type,
      external_ticketing_url: payload.external_ticketing_url,
      start: payload.start,
      end: payload.end,
      timezone: payload.timezone,
      location: '',
      venue: payload.locationBody?.address ?? '',
      city: payload.locationBody?.city ?? '',
      state: payload.locationBody?.state ?? '',
      country: payload.locationBody?.country ?? '',
      event_age: payload.event_age,
      genres: payload.genres,
      medias: payload.medias ?? payload.images.map((url) => ({ file_url: url })),
      tickets: payload.tickets?.map((t, i) => ({ ...t, id: Date.now() + i })),
      guests: payload.guests ?? [],
      collaborators: [],
      is_draft_or_published: payload.is_draft_or_published,
      is_cancelled: false,
      event_creator_id: 1,
      tickets_sold: 0,
    };
    set((state) => ({ events: [newEvent, ...state.events] }));
    return newEvent;
  },

  updateEvent: (id, patch) => {
    set((state) => ({
      events: state.events.map((ev) => (ev.id === id ? { ...ev, ...patch } : ev)),
    }));
  },

  cancelEvent: (id) => {
    set((state) => ({
      events: state.events.map((ev) => (ev.id === id ? { ...ev, is_cancelled: true } : ev)),
    }));
  },

  getEventById: (id) => get().events.find((ev) => ev.id === id),

  getMyEvents: () => get().events.filter((ev) => ev.event_creator_id === 1),
}));
